import React from 'react';
import {
    Autocomplete,
    Box,
    Button,
    List,
    ListItem,
    ListItemText,
    TextField,
    Typography,
} from '@mui/material';
import { sortByString } from '../../utils/sortByString';
import { CompanyPrivilegeFlag } from '../../utils/companyPrivilegeFlags';

function companiesWithFlag(companies, flag) {
    return sortByString(
        (companies ?? []).filter((company) => company?.[flag] === true),
        'name',
    );
}

function companiesWithoutFlag(companies, flag) {
    return sortByString(
        (companies ?? []).filter((company) => company?.[flag] !== true),
        'name',
    );
}

function CompanyPrivilegeColumn({
    title,
    description,
    addLabel,
    flag,
    companies,
    onTogglePrivilege,
}) {
    const [selected, setSelected] = React.useState(null);
    const granted = companiesWithFlag(companies, flag);
    const available = companiesWithoutFlag(companies, flag);

    function addSelected() {
        if (!selected?.id) {
            return;
        }

        onTogglePrivilege({ company: selected, flag, enabled: true });
        setSelected(null);
    }

    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 280,
                border: 'solid darkGray 2px',
                borderRadius: '5px',
                p: 2,
            }}
        >
            <Typography variant="h6">{title}</Typography>
            <Typography sx={{ mb: 2 }}>{description}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                <Autocomplete
                    disablePortal
                    sx={{ flex: 1 }}
                    options={available}
                    value={selected}
                    getOptionLabel={(option) => option.name ?? ''}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    onChange={(event, newValue) => setSelected(newValue)}
                    renderInput={(params) => <TextField {...params} label={addLabel} />}
                />
                <Button variant="contained" disabled={!selected} onClick={addSelected}>
                    Add
                </Button>
            </Box>
            {granted.length === 0 ? (
                <Typography>No companies have this privilege.</Typography>
            ) : (
                <List dense>
                    {granted.map((company) => (
                        <ListItem
                            key={company.id}
                            secondaryAction={
                                <Button
                                    color="error"
                                    onClick={() => onTogglePrivilege({ company, flag, enabled: false })}
                                >
                                    Remove
                                </Button>
                            }
                        >
                            <ListItemText primary={company.name} />
                        </ListItem>
                    ))}
                </List>
            )}
        </Box>
    );
}

export function CompanyPrivilegesView({ companies, onTogglePrivilege }) {
    return (
        <Box>
            <Typography variant="h5" sx={{ mb: 1 }}>
                Company Privileges
            </Typography>
            <Typography sx={{ mb: 2 }}>
                Credit and external processing are company-level privileges. Credit appears in the
                Upload Plan estimate flow at companies where it is turned on.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <CompanyPrivilegeColumn
                    title="External Plan Processors"
                    description="Companies that can process plans for customers and appear as processors."
                    addLabel="Add processor company"
                    flag={CompanyPrivilegeFlag.ProcessExternal}
                    companies={companies}
                    onTogglePrivilege={onTogglePrivilege}
                />
                <CompanyPrivilegeColumn
                    title="Credit"
                    description="Companies whose Upload Plan users can apply plan credits on Botmark Estimates."
                    addLabel="Add credit company"
                    flag={CompanyPrivilegeFlag.CreditEnabled}
                    companies={companies}
                    onTogglePrivilege={onTogglePrivilege}
                />
            </Box>
        </Box>
    );
}
